import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { confirmToast } from '../../utils/confirmToast';

interface UserInfluencer {
  _id: string;
  name: string;
  phoneNumber: string;
  email: string;
  websiteUrl: string;
  instagramHandle: string;
  followers: number;
  message: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const UserInfluencersSection: React.FC = () => {
  const [influencers, setInfluencers] = useState<UserInfluencer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || '';

    // Fetch influencers data
    fetch(`${API_BASE_URL}/api/userinfluencers`)
      .then(res => res.json())
      .then(influencersData => {
        setInfluencers(influencersData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching influencers data:', error);
        setInfluencers([]);
        setLoading(false);
      });
  }, []);

  const updateStatus = async (id: string, newStatus: string) => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || '';

    try {
      const response = await fetch(`${API_BASE_URL}/api/userinfluencers/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        setInfluencers(prev => prev.map(influencer =>
          influencer._id === id ? { ...influencer, status: newStatus } : influencer
        ));
      } else {
        const errorData = await response.json();
        console.error('Failed to update status:', errorData);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteInfluencer = async (id: string) => {
    const confirmed = await confirmToast('Are you sure you want to delete this influencer?', {
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    const API_BASE_URL = import.meta.env.VITE_API_URL || '';

    try {
      const response = await fetch(`${API_BASE_URL}/api/userinfluencers/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setInfluencers(prev => prev.filter(influencer => influencer._id !== id));
        toast.success('Influencer deleted');
      } else {
        const errorData = await response.json();
        console.error('Failed to delete influencer:', errorData);
        toast.error('Failed to delete influencer. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting influencer:', error);
      toast.error('An error occurred while deleting the influencer.');
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";

    switch (status.toLowerCase()) {
      case 'approved':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'rejected':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Influencers</h1>
            <p className="text-gray-600 mt-1">Manage influencer user accounts and applications</p>
          </div>
          <div className="text-center">
            <span className="text-sm text-gray-500">Total Influencers:</span>
            <span className="ml-2 text-lg font-semibold text-blue-600">{influencers.length}</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {influencers.map((influencer) => (
            <div key={influencer._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  {/* Avatar */}
                  <div className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                    👤
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-sm font-medium text-gray-900">{influencer.name}</h3>
                      <span className={getStatusBadge(influencer.status)}>
                        {influencer.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{influencer.email}</p>
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-500">Phone: {influencer.phoneNumber}</p>
                      <p className="text-xs text-gray-500">Website: {influencer.websiteUrl || 'N/A'}</p>
                      {/* <p className="text-xs text-gray-500">Instagram: @{influencer.instagramHandle}</p>
                      <p className="text-xs text-gray-500">Followers: {influencer.followers ? influencer.followers.toLocaleString() : 'N/A'}</p>
                      <p className="text-xs text-gray-500">Message: {influencer.message || 'No message'}</p> */}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <div className="text-xs text-gray-400">
                    {formatDate(influencer.createdAt)}
                  </div>
                  <div className="relative">
                    <select
                      value={influencer.status}
                      onChange={(e) => updateStatus(influencer._id, e.target.value)}
                      className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <button
                    onClick={() => deleteInfluencer(influencer._id)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                    title="Delete influencer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserInfluencersSection;
